import React from "react";
import TextInput from "../../Common/TextInput/TextInput";
import SelectInput from "../../Common/Select-Input/SelectInput";
import OptionsData from "../../Assets/Json/StaticOptions.json";
import CustomTooltip from "../../Common/Tooltip/Tooltip";
import infoIcon from "../../Assets/Images/infoicon.svg";
import DynamicMultipleSelectCheckmarks from "../../Components/Multiple-Select-Checkmarks/MultipleSelectCheckmarks";

const { StaticOptions = {} } = OptionsData || {};
const { staticJobTypes = [], priority = [] } = StaticOptions || {};

function EditSection({
  title,
  children,
  defaultOpen = true,
  allowOverflow = false,
}) {
  const [open, setOpen] = React.useState(defaultOpen);
  return (
    <div
      style={{
        border: "1px solid var(--accordian-border)",
        borderRadius: "12px",
        overflow: allowOverflow ? "visible" : "hidden",
        marginBottom: "16px",
        ...(allowOverflow && { position: "relative", zIndex: 10 }),
      }}
    >
      <div
        onClick={() => setOpen((p) => !p)}
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "16px 20px",
          cursor: "pointer",
          borderBottom: open ? "1px solid var(--accordian-border)" : "none",
          background: "var(--accordian-background)",
          ...(allowOverflow && {
            borderRadius: open ? "12px 12px 0 0" : "12px",
          }),
        }}
      >
        <span
          style={{
            fontWeight: 700,
            fontSize: "15px",
            color: "var(--header__color)",
          }}
        >
          {title}
        </span>
        <span
          style={{
            color: "var(--text-color-accent, #64748B)",
            fontSize: "13px",
          }}
        >
          {open ? "∧" : "∨"}
        </span>
      </div>
      <div
        style={{
          maxHeight: open ? "2000px" : "0",
          overflow: allowOverflow && open ? "visible" : "hidden",
          ...(allowOverflow && { borderRadius: "0 0 12px 12px" }),
          transition:
            "max-height 0.3s cubic-bezier(0.4,0,0.2,1), opacity 0.2s ease",
          opacity: open ? 1 : 0,
          padding: open ? "20px 24px" : "0 24px",
          background: "var(--body__background)",
        }}
      >
        {children}
      </div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <div
        style={{
          fontSize: "12px",
          fontWeight: 500,
          color: "#64748B",
          marginBottom: "6px",
        }}
      >
        {label}
        {required && (
          <span style={{ color: "#ef4444", marginLeft: "2px" }}>*</span>
        )}
      </div>
      {children}
    </div>
  );
}

function JobEditForm({
  formik,
  recruiterOptions = [],
  onRecruiterSearch = () => {},
  onAssignedToChange = () => {},
  isLoadingRecruiters = false,
}) {
  return (
    <div
      style={{
        padding: "14px 14px 0 14px",
        animation: "jobEditSlideDown 0.25s cubic-bezier(0.4,0,0.2,1)",
      }}
    >
      <style>{`
        @keyframes jobEditSlideDown {
          from { opacity: 0; transform: translateY(-10px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      {/* Location & Job Type */}
      <EditSection title="Location & Job Type">
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "16px",
          }}
        >
          <Field label="Location" required>
            <TextInput
              name="jobLocation"
              value={formik?.values?.jobLocation || ""}
              placeholder="Enter job location"
              onBlur={formik?.handleBlur}
              error={
                formik?.touched?.jobLocation && formik?.errors?.jobLocation
              }
              errorMessage={formik?.errors?.jobLocation}
              onChange={formik?.handleChange}
            />
          </Field>

          <Field label="Job Type" required>
            <SelectInput
              options={staticJobTypes}
              name="jobType"
              value={formik?.values?.jobType || ""}
              onBlur={formik?.handleBlur}
              error={formik?.touched?.jobType && formik?.errors?.jobType}
              errorMessage={formik?.errors?.jobType}
              onChange={formik?.handleChange}
              wrapperStyle={{ width: "100%", marginBottom: 0 }}
            />
          </Field>

          <Field label="Job Priority">
            <SelectInput
              options={priority}
              name="jobPriority"
              value={formik?.values?.jobPriority || ""}
              onBlur={formik?.handleBlur}
              error={
                formik?.touched?.jobPriority && formik?.errors?.jobPriority
              }
              errorMessage={formik?.errors?.jobPriority}
              onChange={formik?.handleChange}
              wrapperStyle={{ width: "100%", marginBottom: 0 }}
            />
          </Field>
        </div>
      </EditSection>

      {/* Compensation & Requirements */}
      <EditSection title="Compensation & Requirements">
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "16px",
          }}
        >
          <Field label="Experience (years)" required>
            <div style={{ display: "flex", gap: "10px" }}>
              <div style={{ flex: 1 }}>
                <TextInput
                  name="minYearsExperience"
                  value={formik?.values?.minYearsExperience || ""}
                  placeholder="Min (e.g. 3)"
                  onBlur={formik?.handleBlur}
                  required={true}
                  error={
                    formik?.touched?.minYearsExperience &&
                    formik?.errors?.minYearsExperience
                  }
                  errorMessage={formik?.errors?.minYearsExperience}
                  onChange={formik?.handleChange}
                />
              </div>
              <div style={{ flex: 1 }}>
                <TextInput
                  name="maxYearsExperience"
                  value={formik?.values?.maxYearsExperience || ""}
                  placeholder="Max (e.g. 5)"
                  onBlur={formik?.handleBlur}
                  required={true}
                  error={
                    formik?.touched?.maxYearsExperience &&
                    formik?.errors?.maxYearsExperience
                  }
                  errorMessage={formik?.errors?.maxYearsExperience}
                  onChange={formik?.handleChange}
                />
              </div>
            </div>
          </Field>

          <Field label="Number of Openings" required>
            <TextInput
              name="openings"
              value={formik?.values?.openings || ""}
              placeholder="e.g. 5"
              onBlur={formik?.handleBlur}
              error={formik?.touched?.openings && formik?.errors?.openings}
              errorMessage={formik?.errors?.openings}
              onChange={(e) => {
                const value = e.target.value;
                if (value === "" || /^\d+$/.test(value)) {
                  formik.handleChange(e);
                }
              }}
              inputProps={{
                inputMode: "numeric",
                pattern: "[0-9]*",
                onKeyPress: (e) => {
                  if (!/[0-9]/.test(e.key)) e.preventDefault();
                },
              }}
            />
          </Field>

          <Field
            label={
              <div
                style={{ display: "flex", alignItems: "center", gap: "6px" }}
              >
                <span
                  style={{ display: "flex", alignItems: "center", gap: "4px" }}
                >
                  Salary Min
                </span>

                <CustomTooltip
                  title="Enter the salary range for this position"
                  arrow
                  placement="top"
                >
                  <img
                    src={infoIcon}
                    alt="Info"
                    style={{
                      width: "16px",
                      height: "16px",
                      cursor: "pointer",
                    }}
                  />
                </CustomTooltip>
              </div>
            }
          >
            <TextInput
              name="salary_min"
              value={formik?.values?.salary_min || ""}
              placeholder="Min salary per annum"
              onBlur={formik?.handleBlur}
              error={formik?.touched?.salary_min && formik?.errors?.salary_min}
              errorMessage={formik?.errors?.salary_min}
              onChange={formik?.handleChange}
            />
          </Field>

          <Field
            label={
              <div
                style={{ display: "flex", alignItems: "center", gap: "6px" }}
              >
                <span
                  style={{ display: "flex", alignItems: "center", gap: "4px" }}
                >
                  Salary Max
                </span>
                <CustomTooltip
                  title="Enter the salary range for this position"
                  arrow
                  placement="top"
                >
                  <img
                    src={infoIcon}
                    alt="Info"
                    style={{
                      width: "16px",
                      height: "16px",
                      cursor: "pointer",
                    }}
                  />
                </CustomTooltip>
              </div>
            }
          >
            <TextInput
              name="salary_max"
              value={formik?.values?.salary_max || ""}
              placeholder="Max salary per annum"
              onBlur={formik?.handleBlur}
              error={formik?.touched?.salary_max && formik?.errors?.salary_max}
              errorMessage={formik?.errors?.salary_max}
              onChange={formik?.handleChange}
            />
          </Field>
        </div>
      </EditSection>

      {/* Assigned Recruiters */}
      <EditSection title="Assigned Recruiters" allowOverflow>
        <Field label="Recruiters">
          <DynamicMultipleSelectCheckmarks
            options={recruiterOptions}
            label=""
            labelKey="label"
            valueKey="value"
            value={formik?.values?.assignedTo || []}
            onChange={onAssignedToChange}
            onSearchChange={onRecruiterSearch}
            loading={isLoadingRecruiters}
            placeholder="Select Recruiters"
            width="100%"
          />
        </Field>
      </EditSection>
    </div>
  );
}

export default JobEditForm;
